import { useEffect, useState } from 'react';

import GoBack from '../../components/go-back/go-back';
import { User, UserSummary } from '../../types/user.type';
import { userSdk } from '../../services/user.service';
import ResponseCodes from '../../constants/response-codes.constant';
import { useAuthStore, usePetitionStore } from '../../store';
import Button from '../../components/button/button';
import { petitionSdk } from '../../services/petition.service';
import Logger from '../../utils/logger.util';
import { PetitionMotive } from '../../types/petition-motive.type';
import { useNavigation } from '../../hooks/navigation.hook';
import View from '../../components/view/view';
import { useSettingsStore } from '../../store/settings-store';

import styles from './create-petition.module.scss';
import UserSelection from './user-selection/user-selection';
import ReasonSelection from './reason-selection/reason-selection';
import LocationSelection from './location-selection/location-selection';
import SourceSelection from './source-selection/source-selection';
import { creationSchema } from './utils';

const CreatePetitionView = () => {
    const { navigate } = useNavigation();
    const { accessToken, permissions } = useAuthStore();
    const { theme } = useSettingsStore();
    const { motives } = usePetitionStore();

    const [userList, setUserList] = useState<UserSummary[]>([]);
    const [selectedUser, setSelectedUser] = useState<User>();
    const [selectedMotives, setSelectedMotives] = useState<PetitionMotive[]>(
        []
    );
    const [location, setLocation] = useState({
        lat: 0,
        lng: 0,
        address: '',
    });
    const [channel, setChannel] = useState('');
    const [buttonConfig, setButtonConfig] = useState({
        text: 'Crear solicitud',
        disabled: true,
    });

    useEffect(() => {
        checkForm();
    }, [selectedUser, location, selectedMotives, channel]);

    const searchUser = async (value: string) => {
        const usersList = await userSdk.getAll({
            search: value,
            page: 1,
            count: 10,
        });
        if (!usersList) return;
        setUserList(usersList.result);
    };

    const getUserDetails = async (id: number) => {
        const response = await userSdk.getUserById(id);
        if (response.code !== ResponseCodes.PROCESS_OK) {
            return Logger.error('error fetching user:', response);
        }
        setSelectedUser(response.data);
    };

    const checkForm = async () => {
        const valid = await creationSchema.isValid({
            userId: selectedUser?.id,
            location: location,
            motives: selectedMotives.map((motive) => motive.id),
            channel: channel,
        });
        setButtonConfig({
            text: 'Crear solicitud',
            disabled: !valid,
        });
    };

    const createPetition = async () => {
        setButtonConfig({
            text: 'Creando...',
            disabled: true,
        });
        const payload = {
            userId: selectedUser!.id,
            location: location,
            motives: selectedMotives.map((motive) => motive.id),
            channel: channel,
        };

        const response = await petitionSdk.createPetition(
            payload,
            accessToken!
        );
        if (response.code !== ResponseCodes.CREATED) {
            Logger.error('Error creating petition:', response);
            return setButtonConfig({
                text: 'Crear solicitud',
                disabled: false,
            });
        }
        return navigate('/', { replace: true });
    };

    return (
        <View
            theme={theme}
            permissions={permissions}
            requiredPermission="create-petitions"
        >
            <div
                className={'viewContent'}
                style={{ paddingLeft: 40, paddingRight: 40 }}
            >
                <header>
                    <GoBack theme={theme.current} />
                    <h1 className={'view-title'}>
                        Crear solicitud de emergencia
                    </h1>
                </header>
                <form className={styles.form}>
                    <UserSelection
                        selectedUser={selectedUser}
                        userList={userList}
                        onClear={() => setSelectedUser(undefined)}
                        onSelect={getUserDetails}
                        onSearch={searchUser}
                        onCreated={setSelectedUser}
                    />
                    <LocationSelection onSelect={setLocation} />
                    <ReasonSelection
                        motives={motives}
                        onSelection={setSelectedMotives}
                    />
                    <SourceSelection onSelect={setChannel} />
                    <div style={{ marginTop: 20 }}>
                        <Button
                            onPress={createPetition}
                            disabled={buttonConfig.disabled}
                        >
                            {buttonConfig.text}
                        </Button>
                    </div>
                </form>
            </div>
        </View>
    );
};

export default CreatePetitionView;
